// The desktop apps a person can install, which of them their plan includes, and which installer
// suits the computer they are on. Pure logic, no React and no icons, so `npm test` runs it in Node.
//
// The catalogue lists only builds that exist and are published at
// https://downloads.fowlguard.com/work/<file>. A platform with no build has no entry and
// the page says "Coming soon" for it. When a new build is published, add it here in the same
// change that publishes it (docs/DESKTOP-DOWNLOADS.md says how and in which order).
//
// The `productivity` id, the productivity/ folder on the server and FowlguardProductivity-x64.exe are identifiers
// and keep their names; people read the suite as Fowlguard Work. The name is written out here so Node
// can run this file alone; desktop-apps.test.ts holds it to product-names.ts (FPT-25).

export type DesktopOs = "windows" | "macos" | "linux";
export type DesktopArch = "x64" | "arm64";

export const DESKTOP_OSES: readonly DesktopOs[] = ["windows", "macos", "linux"];

export const OS_LABELS: Record<DesktopOs, string> = { windows: "Windows", macos: "macOS", linux: "Linux" };

export const DOWNLOADS_BASE_URL = "https://downloads.fowlguard.com/work/";

/** The plan's app keys, as GET /api/entitlements returns them (AppEntitlementService.AllApps). */
export type PlanAppKey = "mail" | "drive" | "docs" | "sheet" | "slides" | "pdf" | "connect";

const PLAN_KEYS: readonly PlanAppKey[] = ["mail", "drive", "docs", "sheet", "slides", "pdf", "connect"];

// Identity names apps after the products since the rename; older plans and tokens carry the old
// keys. Both mean the same app (EntitlementServiceCollectionExtensions.TokenGrants).
const KEY_ALIASES: Record<string, PlanAppKey> = {
    meeting: "connect",
    notes: "docs",
    workbook: "sheet",
    sheets: "sheet",
    present: "slides",
    reader: "pdf"
};

/** Canonical plan keys from whatever the source returned; unknown keys are dropped. */
export function normalizePlanKeys(keys: readonly string[]): Set<PlanAppKey> {
    const out = new Set<PlanAppKey>();
    for (const raw of keys) {
        const k = raw.trim().toLowerCase();
        const canonical = KEY_ALIASES[k] ?? (PLAN_KEYS as readonly string[]).find((p) => p === k);
        if (canonical) out.add(canonical as PlanAppKey);
    }
    return out;
}

export interface DesktopBuild {
    os: DesktopOs;
    /** "universal" = one file for every architecture of that OS (a macOS .pkg with both slices, a Linux
     *  .run carrying the amd64 and arm64 packages). */
    arch: DesktopArch | "universal";
    /** Fixed public name under DOWNLOADS_BASE_URL; a `.sha256` of the same name sits beside it. */
    file: string;
}

export interface DesktopApp {
    id: "productivity" | "mail" | "meeting" | "workbook" | "notes" | "present" | "pdf";
    name: string;
    blurb: string;
    /** Every key must be in the plan for the app to be offered. */
    planKeys: readonly PlanAppKey[];
    builds: readonly DesktopBuild[];
}

// No published name carries a version: a fixed URL builds reputation with SmartScreen, Gatekeeper and
// Safe Browsing, as Fowlguard Secure's -macOS.pkg and -Linux.run names do. macOS is one universal .pkg
// (MacOS/installer/build-pkg.sh); Linux is one .run carrying the amd64 and arm64 .debs, which installs
// the one the machine needs (Linux/installer/build-linux.sh). The suite gets a build on an OS only once
// every app it installs has one there (desktop-apps.test.ts holds it to that).
export const DESKTOP_APPS: readonly DesktopApp[] = [
    {
        id: "productivity",
        name: "Fowlguard Work",
        blurb: "Mail, Meeting, Workbook, Notes, Present and PDF in one installer. Pick the ones you want.",
        planKeys: ["mail", "connect", "sheet", "docs", "slides", "pdf"],
        builds: [{ os: "windows", arch: "x64", file: "FowlguardProductivity-x64.exe" }]
    },
    {
        id: "mail",
        name: "Mail",
        blurb: "Inbox, calendar and tasks",
        planKeys: ["mail"],
        builds: [{ os: "windows", arch: "x64", file: "FowlguardMail-x64.exe" }]
    },
    {
        id: "meeting",
        name: "Meeting",
        blurb: "Chat, channels and meetings",
        planKeys: ["connect"],
        builds: [{ os: "windows", arch: "x64", file: "FowlguardMeeting-x64.exe" }]
    },
    {
        id: "workbook",
        name: "Workbook",
        blurb: "Spreadsheets and data",
        planKeys: ["sheet"],
        builds: [{ os: "windows", arch: "x64", file: "FowlguardWorkbook-x64.exe" }]
    },
    {
        id: "notes",
        name: "Notes",
        blurb: "Documents and notes",
        planKeys: ["docs"],
        builds: [{ os: "windows", arch: "x64", file: "FowlguardNotes-x64.exe" }]
    },
    {
        id: "present",
        name: "Present",
        blurb: "Slides and decks",
        planKeys: ["slides"],
        builds: [{ os: "windows", arch: "x64", file: "FowlguardPresent-x64.exe" }]
    },
    {
        id: "pdf",
        name: "PDF",
        blurb: "Read, fill, sign and edit PDFs",
        planKeys: ["pdf"],
        builds: [{ os: "windows", arch: "x64", file: "FowlguardPDF-x64.exe" }]
    }
];

export function downloadUrl(build: DesktopBuild): string {
    return DOWNLOADS_BASE_URL + build.file;
}

export function checksumUrl(build: DesktopBuild): string {
    return downloadUrl(build) + ".sha256";
}

// ── Which apps the plan includes ─────────────────────────────────────────

/**
 * The apps this person may install. `granted` is the plan's key list, or null when it could not
 * be read: then every app is offered, the same fail-open rule AppEntitlementService applies, so an
 * entitlement outage never hides the installers (the apps themselves still check on sign-in).
 */
export function appsForPlan(granted: readonly string[] | null, apps: readonly DesktopApp[] = DESKTOP_APPS): DesktopApp[] {
    if (granted === null) return [...apps];
    const plan = normalizePlanKeys(granted);
    return apps.filter((a) => a.planKeys.every((k) => plan.has(k)));
}

// ── Which computer this is ───────────────────────────────────────────────

export interface Platform {
    /** "other" = a phone, tablet, ChromeOS or anything without a desktop build family. */
    os: DesktopOs | "other";
    arch: DesktopArch | "unknown";
}

/** What the browser tells us. Every field is optional so tests can pass just a user agent. */
export interface PlatformHints {
    userAgent?: string;
    /** navigator.userAgentData (Chromium), if present. */
    uaPlatform?: string;
    uaMobile?: boolean;
    /** From navigator.userAgentData.getHighEntropyValues(["architecture", "bitness"]). */
    architecture?: string;
    bitness?: string;
    /** navigator.maxTouchPoints: iPadOS reports itself as a Mac, but a Mac has no touch screen. */
    maxTouchPoints?: number;
}

export function detectPlatform(h: PlatformHints): Platform {
    const ua = h.userAgent ?? "";
    const uaPlatform = (h.uaPlatform ?? "").toLowerCase();

    if (h.uaMobile || /Android|iPhone|iPad|iPod|Mobile/i.test(ua)) return { os: "other", arch: "unknown" };

    let os: Platform["os"];
    if (uaPlatform === "windows" || /Windows NT|Win64|Win32/i.test(ua)) os = "windows";
    else if (uaPlatform === "chrome os" || uaPlatform === "chromeos" || /CrOS/.test(ua)) os = "other";
    else if (uaPlatform === "macos" || /Macintosh|Mac OS X/.test(ua)) os = (h.maxTouchPoints ?? 0) > 1 ? "other" : "macos";
    else if (uaPlatform === "linux" || /Linux|X11/.test(ua)) os = "linux";
    else os = "other";

    if (os === "other") return { os, arch: "unknown" };

    // Client hints are the only reliable source on Chromium (the user agent is frozen there).
    const archHint = (h.architecture ?? "").toLowerCase();
    if (archHint === "arm" && h.bitness !== "32") return { os, arch: "arm64" };
    if (archHint === "x86" && h.bitness !== "32") return { os, arch: "x64" };

    if (os === "windows") {
        if (/ARM64|aarch64/i.test(ua)) return { os, arch: "arm64" };
        if (/Win64|x64|WOW64|amd64/i.test(ua)) return { os, arch: "x64" };
    }
    if (os === "linux") {
        if (/aarch64|arm64/i.test(ua)) return { os, arch: "arm64" };
        if (/x86_64|amd64/i.test(ua)) return { os, arch: "x64" };
    }
    // Every Mac browser says "Intel Mac OS X", Apple Silicon included, so a Mac's arch is
    // unknown without client hints; macOS builds are universal, so it does not matter there.
    return { os, arch: "unknown" };
}

/** Reads the hints from the running browser. Never throws; resolves without high-entropy hints if refused. */
export async function readPlatformHints(nav: Navigator | undefined = typeof navigator === "undefined" ? undefined : navigator): Promise<PlatformHints> {
    if (!nav) return {};
    const data = (nav as Navigator & {
        userAgentData?: {
            platform?: string;
            mobile?: boolean;
            getHighEntropyValues?: (hints: string[]) => Promise<{ architecture?: string; bitness?: string }>;
        };
    }).userAgentData;
    const hints: PlatformHints = {
        userAgent: nav.userAgent,
        uaPlatform: data?.platform,
        uaMobile: data?.mobile,
        maxTouchPoints: nav.maxTouchPoints
    };
    try {
        const high = await data?.getHighEntropyValues?.(["architecture", "bitness"]);
        if (high) {
            hints.architecture = high.architecture;
            hints.bitness = high.bitness;
        }
    } catch {
        /* the browser declined: fall back to the user agent */
    }
    return hints;
}

// ── Which installer, and in what order ───────────────────────────────────

export interface BuildChoice {
    build: DesktopBuild;
    /** An x64 build offered to a Windows on Arm computer, which runs it through emulation. */
    emulated: boolean;
}

/** The installer for `app` on `os`/`arch`, or null when that OS has no build yet ("Coming soon"). */
export function chooseBuild(app: DesktopApp, os: DesktopOs, arch: Platform["arch"]): BuildChoice | null {
    const forOs = app.builds.filter((b) => b.os === os);
    if (forOs.length === 0) return null;
    const exact = arch === "unknown" ? undefined : forOs.find((b) => b.arch === arch);
    if (exact) return { build: exact, emulated: false };
    const universal = forOs.find((b) => b.arch === "universal");
    if (universal) return { build: universal, emulated: false };
    const x64 = forOs.find((b) => b.arch === "x64");
    if (x64) return { build: x64, emulated: os === "windows" && arch === "arm64" };
    return { build: forOs[0] as DesktopBuild, emulated: false };
}

export interface PlatformSection {
    os: DesktopOs;
    entries: Array<{ app: DesktopApp; choice: BuildChoice | null }>;
}

export interface InstallLayout {
    /** This computer's OS first; null for a phone, tablet or unknown system. */
    current: PlatformSection | null;
    /** Every other desktop OS, in DESKTOP_OSES order. */
    others: PlatformSection[];
}

export function installLayout(apps: readonly DesktopApp[], platform: Platform): InstallLayout {
    const section = (os: DesktopOs): PlatformSection => ({
        os,
        entries: apps.map((app) => ({ app, choice: chooseBuild(app, os, os === platform.os ? platform.arch : "unknown") }))
    });
    const current = platform.os === "other" ? null : section(platform.os);
    const others = DESKTOP_OSES.filter((os) => os !== platform.os).map(section);
    return { current, others };
}
